<template>
    <div id="task-list-view">
        <div class="kanban-task-table">
            <supsis-table
                :columns="columns"
                :rows="filterTasks"
                :bottom-pagination="true"
                :pagination-cursor="paginationCursor"
                :options="tableOptions"
                @cursor-changed="(cursor) => (paginationCursor = cursor)"
            />
        </div>
    </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Avatar from "vue-avatar";
import { getOrUpdateFilters } from "../../utils/persistenceStorage";
import SupsisTable from "../SupsisTable.vue";
import LowIcon from "../svg/task/LowIcon.vue";
import LowestIcon from "../svg/task/LowestIcon.vue";
import MediumIcon from "../svg/task/MediumIcon.vue";
import HighIcon from "../svg/task/HighIcon.vue";
import HighestIcon from "../svg/task/HighestIcon.vue";
import SupsisButton from "../../views/Pages/Settings/components/SupsisButton.vue";
import { deleteTask } from "../../api/tasks";

const filters = getOrUpdateFilters("kanbanlist");
export default {
    name: "TaskArchiveList",
    components: {
        SupsisTable,
        Avatar,
        LowIcon,
        LowestIcon,
        SupsisButton,
        MediumIcon,
        HighIcon,
        HighestIcon,
    },
    props: {
        visitorId: {
            type: String,
            default: null,
        },
        assignees: {
            type: Array,
            default: () => [],
        },
        owners: {
            type: Array,
            default: () => [],
        },
        followers: {
            type: Array,
            default: () => [],
        },
        workflowId: {
            type: String,
            default: null,
        },
        showOverdue: {
            type: Boolean,
            default: false,
        },
        search: {
            type: String,
            default: '',
        },
        selectedPriorities: {
            type: Array,
            default: () => [],
        },
        startDateRange: {
            type: Array,
            default: () => null,
        },
        endDateRange: {
            type: Array,
            default: () => null,
        },
        dateRange: {
            type: Array,
            default: () => null,
        },
        createdBy: {
            type: Array,
            default: () => [],
        },
        selectedTags: {
            type: Array,
            default: () => [],
        },
        updateAtRange: {
            type: Array,
            default: () => null,
        },
    },
    data() {
        return {
            columns: [

                {
                    title: this.$t("message.createdDate"),
                    field: "createdAt",
                    key: "createdAt",
                    renderBodyCell: ({ row }) => {
                        return this.formatDate(row.createdAt);
                    },
                },
                {
                    title: this.$t("task.archivingDate"),
                    field: "archivedDate",
                    key: "archivedDate",
                    renderBodyCell: ({ row }) => {
                        return row.dates.archivedDate ? this.formatDate(row.dates.archivedDate) : "";
                    },
                },
                {
                    title: this.$t("message.workflow"),
                    field: "workflow",
                    key: "workflow",
                    renderBodyCell: ({ row }) => {
                        const workflow = this.workflows[row.workflow._id];
                        return workflow ? workflow.title : "";
                    },
                },
                {
                    // title: this.$t("message.name"),
                    title: this.$t("task.task"),
                    field: "title",
                    key: "title",
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class={"title-cell"} v-tippy={{ content: row.title }}>
                                {row.title}
                            </div>
                        );
                    },
                },
                {
                    title: this.$t("task.owner"),
                    field: "owner",
                    key: "owner",
                    renderBodyCell: ({ row }) => {
                        return (
                            row.owner && this.users[row.owner] && (
                                <div class="agent-detail">
                                    <Avatar
                                        size={25}
                                        key={row.owner}
                                        src={this.users[row.owner].gravatar}
                                        username={this.users[row.owner].name}
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                        }}
                                        content={this.users[row.owner].email}
                                    />
                                    <div v-tippy={{ content: this.users[row.owner].name }}>
                                        {this.users[row.owner].name.substring(0, 8) + "..."}
                                    </div>
                                </div>
                            )
                        );
                    },
                },
                {
                    title: this.$t("task.assignees"),
                    field: "assignees",
                    key: "assignees",
                    renderBodyCell: ({ row }) => {
                        return (
                            row.assignees &&
                            row.assignees.length > 0 &&
                            this.users[row.assignees[0]] && (
                                <div class="agent-detail">
                                    <Avatar
                                        size={25}
                                        key={row.assignees[0]}
                                        src={this.users[row.assignees[0]].gravatar}
                                        username={this.users[row.assignees[0]].name}
                                        v-tippy={{
                                            followCursor: true,
                                            placement: "bottom-end",
                                        }}
                                        content={this.users[row.assignees[0]].email}
                                    />
                                    <div v-tippy={{ content: this.users[row.assignees[0]].name }}>
                                        {this.users[row.assignees[0]].name.substring(0, 8) + "..."}
                                    </div>
                                </div>
                            )
                        );
                    },
                },
                {
                    title: this.$t("message.startDate"),
                    field: "startDate",
                    key: "startDate",
                    renderBodyCell: ({ row }) => {
                        return this.formatDate(row.startDate);
                    },
                },
                {
                    title: this.$t("message.priority"),
                    field: "priority",
                    key: "priority",
                    renderBodyCell: ({ row }) => {
                        return row.priority === "low" ? (
                            <LowIcon />
                        ) : row.priority === "lowest" ? (
                            <LowestIcon />
                        ) : row.priority === "medium" ? (
                            <MediumIcon />
                        ) : row.priority === "high" ? (
                            <HighIcon />
                        ) : row.priority === "highest" ? (
                            <HighestIcon />
                        ) : (
                            ""
                        );
                    },
                },
                {
                    title: this.$t("message.endDate"),
                    field: "endDate",
                    key: "endDate",
                    renderBodyCell: ({ row }) => {
                        return row.endDate ? this.formatDate(row.endDate) : "";
                    },
                },
                {

                    title: this.$t("message.transactions"),
                    field: "_id",
                    key: "_id",
                    copyDisabled: true,
                    renderBodyCell: ({ row }) => {
                        return (
                            <div class="task-transactions">
                                <SupsisButton
                                    xxsmall
                                    icon="edit"
                                    onClick={() => {
                                        this.addNewWindow({
                                            type: "editor-task",
                                            title: row.title,
                                            payload: {
                                                taskId: row._id,
                                            },
                                        });
                                    }}
                                >
                                </SupsisButton>
                                <SupsisButton xxsmall icon="delete" danger onClick={() => this.confirmDeleteTask(row)}>
                                </SupsisButton>
                            </div>
                        );
                    },
                },
            ],
            tasks: [],
            paginationCursor: {
                total: 0,
                page: filters.page || 1,
                perPage: filters.perPage || 20,
            },
            tableOptions: {
                bodyCellEvents: ({ row, column, rowIndx }) => {
                    return {
                        dblclick: (event) => {
                            this.addNewWindow({
                                type: "edit-workflow",
                                title: row.title,
                                payload: {
                                    taskId: row._id,
                                },
                            });
                        },
                    };
                },
            },
        };
    },
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        filterTasks() {
            return this.tasks.filter((task) => {
                const visibility = task.workflow && task.workflow.visibility ? task.workflow.visibility : {};
                const users = visibility.users || [];
                const departments = visibility.departments || [];
                const userSubjects = this.user.subjects || [];
                const userId = this.user._id || null;

                let permissionAllowed = false;
                if (users.includes(userId) && departments.length === 0) {
                    permissionAllowed = true;
                } else if (departments.some((dept) => userSubjects.includes(dept)) && users.length === 0) {
                    permissionAllowed = true;
                } else if (users.includes(userId) || departments.some((dept) => userSubjects.includes(dept))) {
                    permissionAllowed = true;
                } else if (users.length === 0 && departments.length === 0) {
                    permissionAllowed = true;
                }

                if (!permissionAllowed) return false;

                if (this.search && this.search.trim() !== '') {
                    const searchLower = this.search.toLowerCase();
                    const titleMatch = task.title && task.title.toLowerCase().includes(searchLower);
                    const customIdMatch = task.customId && task.customId.toLowerCase().includes(searchLower);

                    if (!titleMatch && !customIdMatch) return false;
                }

                if (this.selectedPriorities && this.selectedPriorities.length > 0) {
                    if (!this.selectedPriorities.includes(task.priority)) return false;
                }

                if (this.owners && this.owners.length > 0) {
                    if (!task.owner) return false;

                    const ownerMatch = (typeof task.owner === 'object' && task.owner && task.owner._id)
                        ? this.owners.includes(task.owner._id)
                        : (typeof task.owner === 'string' && this.owners.includes(task.owner));

                    if (!ownerMatch) return false;
                }

                if (this.followers && this.followers.length > 0) {
                    if (!task.followers || !Array.isArray(task.followers) || !task.followers.length) return false;

                    const hasFollower = task.followers.some(follower =>
                        follower && (
                            (typeof follower === 'object' && follower._id && this.followers.includes(follower._id)) ||
                            (typeof follower === 'string' && this.followers.includes(follower))
                        )
                    );

                    if (!hasFollower) return false;
                }

                if (this.createdBy && this.createdBy.length > 0) {
                    if (!task.createdBy) return false;

                    const systemTypes = ['api', 'chatbot', 'automation'];
                    const systemTypeFilters = this.createdBy.filter(type => systemTypes.includes(type));
                    const userFilters = this.createdBy.filter(type => !systemTypes.includes(type));

                    let systemTypeMatch = true;
                    let userMatch = true;

                    if (systemTypeFilters.length > 0) {
                        systemTypeMatch = task.createdBy &&
                                          task.createdBy.type &&
                                          systemTypeFilters.includes(task.createdBy.type);
                    }

                    if (userFilters.length > 0) {
                        userMatch = task.createdBy &&
                                    task.createdBy.type === 'user' &&
                                    task.createdBy.user && (
                                        (typeof task.createdBy.user === 'object' && task.createdBy.user._id && userFilters.includes(task.createdBy.user._id)) ||
                                        (typeof task.createdBy.user === 'string' && userFilters.includes(task.createdBy.user))
                                    );
                    }

                    if (systemTypeFilters.length > 0 && userFilters.length > 0) {
                        if (!systemTypeMatch && !userMatch) return false;
                    } else if (systemTypeFilters.length > 0) {
                        if (!systemTypeMatch) return false;
                    } else if (userFilters.length > 0) {
                        if (!userMatch) return false;
                    }
                }

                if (this.selectedTags && this.selectedTags.length > 0) {
                    if (!task.tags || !Array.isArray(task.tags) || !task.tags.length) return false;

                    if (!task.tags.some(tag => tag && tag._id && this.selectedTags.includes(tag._id))) {
                        return false;
                    }
                }

                if (this.dateRange && this.dateRange.length === 2) {
                    if (!task.createdAt) return false;

                    const startDate = new Date(this.dateRange[0]);
                    const endDate = new Date(this.dateRange[1]);
                    endDate.setHours(23, 59, 59, 999);

                    const taskCreatedAt = new Date(task.createdAt);
                    if (taskCreatedAt < startDate || taskCreatedAt > endDate) return false;
                }

                if (this.startDateRange && this.startDateRange.length === 2) {
                    if (!task.startDate) return false;

                    const startDate = new Date(this.startDateRange[0]);
                    const endDate = new Date(this.startDateRange[1]);
                    endDate.setHours(23, 59, 59, 999);

                    const taskStartDate = new Date(task.startDate);
                    if (taskStartDate < startDate || taskStartDate > endDate) return false;
                }

                if (this.endDateRange && this.endDateRange.length === 2) {
                    if (!task.endDate) return false;

                    const startDate = new Date(this.endDateRange[0]);
                    const endDate = new Date(this.endDateRange[1]);
                    endDate.setHours(23, 59, 59, 999);

                    const taskEndDate = new Date(task.endDate);
                    if (taskEndDate < startDate || taskEndDate > endDate) return false;
                }

                if (this.updateAtRange && this.updateAtRange.length === 2) {
                    if (!task.dates || !task.dates.updateDate) return false;

                    const startDate = new Date(this.updateAtRange[0]);
                    const endDate = new Date(this.updateAtRange[1]);
                    endDate.setHours(23, 59, 59, 999);

                    const taskUpdateDate = new Date(task.dates.updateDate);
                    if (taskUpdateDate < startDate || taskUpdateDate > endDate) return false;
                }

                if (this.showOverdue) {
                    if (!task.endDate || new Date(task.endDate) >= new Date()) return false;
                }

                return true;
            });
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            getListTasks: "tasks/getListTasks",
            getListArchiveTasks: "tasks/getListArchiveTasks",
        }),
        formatDate(date) {
            return date && this.$moment(date).format("YYYY/MM/DD HH:mm");
        },
        getTasks() {
            getOrUpdateFilters("kanbanlist", {
                assignees: this.assignees,
                owners: this.owners,
                followers: this.followers,
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
                search: this.search,
                selectedPriorities: this.selectedPriorities,
                selectedTags: this.selectedTags,
                dateRange: this.dateRange,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                showOverdue: this.showOverdue
            });

            this.getListArchiveTasks({
                assignees: this.assignees,
                owners: this.owners,
                followers: this.followers,
                page: this.paginationCursor.page,
                perPage: this.paginationCursor.perPage,
                visitorId: this.visitorId,
                search: this.search,
                priorities: this.selectedPriorities,
                dateRange: this.dateRange,
                createdBy: this.createdBy,
                tags: this.selectedTags,
                startDateRange: this.startDateRange,
                endDateRange: this.endDateRange,
                updateAtRange: this.updateAtRange,
                showOverdue: this.showOverdue,
                workflowId: this.workflowId
            }).then(({ data, total }) => {
                this.tasks = data;
                this.paginationCursor.total = total;
            })
            .catch(error => {
                this.$notification.error({
                    message: 'Hata',
                    description: 'Arşivlenmiş görevler yüklenirken bir hata oluştu.'
                });
            });
        },
        confirmDeleteTask(task) {
            this.$confirm({
                title: "Silme İşlemi",
                content: "Bu görevi silmek istediğinize emin misiniz?",
                okText: this.$t("message.yes"),
                cancelText: this.$t("message.no"),
                onOk: () => {
                    this.deleteTask(task._id);
                },
            });
        },
        deleteTask(taskId) {
            deleteTask(taskId)
                .then(() => {
                    this.getTasks();
                    this.$message.success(`Görev başarıyla silindi`);
                })
                .catch((error) => {
                    console.error("Görev silinirken hata oluştu:", error);
                    this.$message.error(`Görev silinirken hata oluştu`);
                });
        },
    },
    mounted() {
        this.getTasks();
    },
    watch: {
        paginationCursor: {
            deep: true,
            handler() {
                this.getTasks();
            },
        },
        assignees: {
            deep: true,
            handler() {
                this.getTasks();
            },
        },
        owners: {
            deep: true,
            handler() {
                this.getTasks();
            },
        },
        followers: {
            deep: true,
            handler() {
                this.getTasks();
            },
        },
        search: {
            handler(newVal) {
                this.getTasks();
            }
        },
        selectedPriorities: {
            handler() {
                this.getTasks();
            },
            deep: true
        },
        createdBy: {
            handler() {
                this.getTasks();
            },
            deep: true
        },
        selectedTags: {
            handler() {
                this.getTasks();
            },
            deep: true
        },
        dateRange: {
            handler() {
                this.getTasks();
            }
        },
        startDateRange: {
            handler() {
                this.getTasks();
            }
        },
        endDateRange: {
            handler() {
                this.getTasks();
            }
        },
        updateAtRange: {
            handler() {
                this.getTasks();
            }
        },
        showOverdue: {
            handler() {
                this.getTasks();
            }
        },
        workflowId: {
            handler() {
                this.getTasks();
            }
        }
    },
};
</script>
<style lang="scss">
#task-list-view {
    .task-transactions {
        display: flex;
        gap: 5px;
        align-items: center;

        .supsis-button {
            padding: 5px !important;
            height: 27px !important;

            .material-icons {
                font-size: 15px;
            }
        }
    }

    .kanban-task-search {
        display: flex;
        justify-content: center;
        margin-top: 9px;
    }

    .title-cell {
        max-width: 300px;
        -webkit-line-clamp: 1;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .kanban-task-table {
        padding: 10px;
    }


    .supsis-tag-el {
        padding: 4px 10px;
        border: 1px solid var(--pipeline-color);
        color: var(--pipeline-color);
        border-radius: 100px;
        width: max-content;
    }

    .agent-detail {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .paginator {
        padding: 10px;
        display: flex;
        align-items: center;
        justify-content: end;
    }
}
</style>
